import {
  IListener,
  TQuery,
  IListenerCallback,
  TQueryComplete,
  IEvent,
  t_status,
  ICallerCallback,
  TEventTrap
} from '../types/event_type';

import { Codex } from './codex';

const noop = (): void => {};
const ensureArray = <T>(t: T | T[]): T[] => (Array.isArray(t) ? t : [t]);

/**
 * used to capture and query a stream of events
 * useful for:
 * - error messaging
 * - logging
 * - debugging/auditing
 */
export class EventSourceStream {
  /**
   * all distributed reporters
   */
  reporters: Record<string, (event: TEventTrap) => TEventTrap>;

  constructor(private __codex: Codex = new Codex(), private __listeners: IListener[] = [], private __order = 0) {
    this.__codex.link(this);
  }

  get codex() {
    return this.__codex;
  }

  /**
   * start the order count over
   */
  reset() {
    this.__order = 0;
  }

  /**
   * create a new event source database
   */
  build() {
    return new EventSourceStream(this.codex);
  }

  /**
   * create a new event source database with the same listeners
   */
  clone() {
    return new EventSourceStream(this.codex, this.__listeners);
  }

  /**
   * trapped an event as a success
   */
  success(_event: TEventTrap, data: unknown = {}, cb?: ICallerCallback) {
    return this.trap(_event, 'success', data, cb);
  }

  /**
   * trapped an event as a info
   */
  info(_event: TEventTrap, data: unknown = {}, cb?: ICallerCallback) {
    return this.trap(_event, 'info', data, cb);
  }

  /**
   * trapped an event as a success
   */
  warning(_event: TEventTrap, data: unknown = {}, cb?: ICallerCallback) {
    return this.trap(_event, 'warning', data, cb);
  }

  /**
   * trapped an event as a failure
   */
  failure(_event: TEventTrap, data: unknown = {}, cb?: ICallerCallback) {
    return this.trap(_event, 'failure', data, cb);
  }

  /**
   * trapped an event as a failure
   */
  fatal(_event: TEventTrap, data: unknown = {}, cb?: ICallerCallback) {
    return this.trap(_event, 'fatal', data, cb);
  }

  /**
   * @deprecated use #trap
   */
  capture(_event: TEventTrap, status: t_status = 'success', data: unknown = {}, cb?: ICallerCallback): this {
    return this.trap(_event, status, data, cb);
  }

  /**
   * tap a sequence of processes around a single entity, for use with `f1.serial` to log each step
   */
  tap<E>(entity: E, _event: TEventTrap): Promise<E> {
    this.trap({
      ..._event,
      source: _event.source || entity['id'] || '??',
      status: _event.status || 'info',
      data: _event.data || entity
    });

    return Promise.resolve(entity);
  }

  /**
   * capture an event, if any property matches existing listeners, then invoke all callbacks
   *
   * @param _event
   * @param status
   * @param data
   * @param cb
   */
  trap(_event: TEventTrap, status: t_status = 'success', data: unknown = {}, cb?: ICallerCallback): this {
    _event.status = _event.status || status;
    _event.data = _event.data || data;
    _event.ns = _event.ns || '';
    _event.order = this.__order;

    this.__order += 1;
    return this._notify(_event as IEvent, cb);
  }

  /**
   * invoke all listeners which are permitted
   * @param event
   */
  protected _notify(event: IEvent, cb: ICallerCallback = noop): this {
    const matchedQueries = this._findQueryListeners(event);
    let ignoreRule: TQueryComplete | boolean | void;

    for (let tmp, k = 0, n = matchedQueries.length; k < n; k++) {
      if (ignoreRule === false) {
        break;
      } else if (!ignoreRule || ignoreRule === true || !this._matchOne(event, ignoreRule)) {
        tmp = matchedQueries[k].cb(event, cb);
        ignoreRule = tmp && typeof tmp !== 'boolean' ? this._toQueryComplete(tmp) : tmp;
      }
    }

    return this;
  }

  /**
   * match all listeners to the event
   * @param event
   */
  protected _findQueryListeners(event: IEvent): IListener[] {
    return this.__listeners.filter((listener) => {
      return this._matchOne(event, listener.query);
    });
  }

  /**
   * match one query to one event
   * @param event
   * @param query
   */
  protected _matchOne(event: IEvent, query: TQueryComplete | boolean) {
    if (typeof query === 'boolean') {
      return query;
    }

    const wouldSendToSelf = event.producer && query.producer && query.producer.includes(event.producer);
    if (wouldSendToSelf) {
      return false;
    }

    if (query['order'] != null) {
      const start = query.order[0] as number;
      const end = query.order[1] as number;
      const order = event.order;

      const orderIsUnder = start != null && start > order;
      const orderIsOver = end != null && end < order;

      return !orderIsUnder && !orderIsOver;
    }

    for (const k in query) {
      if (k === 'producer') {
        continue;
      }

      const queryConstraint = query[k];
      if (!queryConstraint) {
        return false;
      }

      const eventValue = event[k];
      let hasMatch = false;

      for (const c in queryConstraint) {
        const _queryConstraint = queryConstraint[c];

        if (typeof _queryConstraint === 'string') {
          const hasNot = _queryConstraint[0] === '!';
          const hasValueMatch = !hasNot && _queryConstraint === eventValue;
          const hasNotValueMatch = hasNot && _queryConstraint.slice(1) !== eventValue;

          if (hasValueMatch || hasNotValueMatch) {
            hasMatch = true;
            break;
          }
        } else if (_queryConstraint instanceof RegExp) {
          if (`${eventValue}`.match(_queryConstraint) !== null) {
            hasMatch = true;
            break;
          }
        }
      }

      if (!hasMatch) {
        return false;
      }
    }

    return true;
  }

  /**
   * add a listener to match a range of values
   */
  on(_query: TQuery | boolean, cb: IListenerCallback, priority?: number): IListenerCallback {
    const query = typeof _query === 'boolean' ? _query : this._toQueryComplete(_query);

    this.__listeners.push({
      priority: priority || 0,
      query,
      cb
    });

    if (typeof priority === 'number') {
      this.__listeners.sort((a, b) => b.priority - a.priority);
    }

    return cb;
  }

  /**
   * complete a query so it has all arrays to match against each property
   * @param _query
   */
  protected _toQueryComplete(_query: TQuery): TQueryComplete {
    const query: TQueryComplete = {};

    for (const k in _query) {
      query[k] = ensureArray(_query[k]);
    }

    return query;
  }

  /**
   * remove all listeners for a query based on the callback
   * @param query
   */
  off(cb: IListenerCallback): this {
    this.__listeners = this.__listeners.filter((listener) => {
      return listener.cb !== cb;
    });

    return this;
  }
}
